import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import BrutalButton from "@/components/common/BrutalButton";
import BrutalCard from "@/components/common/BrutalCard";

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [posts, setPosts] = useState([]);
    const [form, setForm] = useState({ title: "", excerpt: "", body: "", tag: "Insight", cover_image: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user || typeof user !== "object") {
            navigate("/login");
            return;
        }
        if (user.role !== "admin") {
            toast.error("Admin access only");
            navigate("/");
            return;
        }
        Promise.all([
            api.get("/contact").then((r) => setMessages(r.data)).catch(() => {}),
            api.get("/blog/posts").then((r) => setPosts(r.data)).catch(() => {}),
        ]);
    }, [user, loading, navigate]);

    const createPost = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/blog/posts", form);
            toast.success("Post published");
            setPosts((p) => [data, ...p]);
            setForm({ title: "", excerpt: "", body: "", tag: "Insight", cover_image: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    if (loading || !user || typeof user !== "object") return null;

    return (
        <section data-testid="admin-page" className="py-20 md:py-28">
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                    <div>
                        <span className="label-mono bg-brand-yellow border-2 border-ink rounded-full px-3 py-1 inline-block">
                            Admin
                        </span>
                        <h1 className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tighter">
                            Studio dashboard
                        </h1>
                    </div>
                    <p className="font-body text-ink/65">
                        Signed in as <strong>{user.name}</strong>
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8">
                    <BrutalCard bg="white" className="lg:col-span-5">
                        <h2 className="font-display text-2xl font-black">New blog post</h2>
                        <form onSubmit={createPost} className="mt-5 space-y-4" data-testid="admin-post-form">
                            <input
                                required
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="Title"
                                className="brutal-input w-full"
                                data-testid="admin-input-title"
                            />
                            <input
                                value={form.tag}
                                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                                placeholder="Tag (e.g. Marketing)"
                                className="brutal-input w-full"
                                data-testid="admin-input-tag"
                            />
                            <input
                                value={form.cover_image}
                                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                                placeholder="Cover image URL (optional)"
                                className="brutal-input w-full"
                                data-testid="admin-input-cover"
                            />
                            <textarea
                                required
                                rows={2}
                                value={form.excerpt}
                                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                                placeholder="Short excerpt"
                                className="brutal-input w-full resize-none"
                                data-testid="admin-input-excerpt"
                            />
                            <textarea
                                required
                                rows={6}
                                value={form.body}
                                onChange={(e) => setForm({ ...form, body: e.target.value })}
                                placeholder="Body (paragraphs separated by blank lines)"
                                className="brutal-input w-full resize-none"
                                data-testid="admin-input-body"
                            />
                            <BrutalButton
                                type="submit"
                                variant="primary"
                                disabled={busy}
                                className="w-full"
                                data-testid="admin-publish-button"
                            >
                                {busy ? "Publishing…" : "Publish post"}
                            </BrutalButton>
                        </form>
                    </BrutalCard>

                    <div className="lg:col-span-7 space-y-8">
                        <BrutalCard bg="cream">
                            <h2 className="font-display text-2xl font-black">Contact messages ({messages.length})</h2>
                            <div className="mt-5 space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="admin-messages">
                                {messages.length === 0 && <p className="font-body text-ink/60">No messages yet.</p>}
                                {messages.map((m) => (
                                    <div key={m.id} className="bg-white border-2 border-ink rounded-lg p-4">
                                        <div className="flex flex-wrap justify-between gap-2 label-mono">
                                            <span>{m.name} · {m.email}</span>
                                            <span>{m.service}</span>
                                        </div>
                                        <p className="mt-2 font-body text-sm">{m.message}</p>
                                    </div>
                                ))}
                            </div>
                        </BrutalCard>

                        <BrutalCard bg="pink">
                            <div className="flex justify-between items-center">
                                <h2 className="font-display text-2xl font-black">Posts ({posts.length})</h2>
                                <Link to="/blog" className="label-mono underline underline-offset-4">View blog</Link>
                            </div>
                            <ul className="mt-5 space-y-2" data-testid="admin-posts-list">
                                {posts.map((p) => (
                                    <li key={p.id} className="bg-white border-2 border-ink rounded-lg px-4 py-3 flex justify-between gap-3">
                                        <Link to={`/blog/${p.slug}`} className="font-display font-bold hover:underline">{p.title}</Link>
                                        <span className="label-mono text-ink/60">{p.tag}</span>
                                    </li>
                                ))}
                            </ul>
                        </BrutalCard>
                    </div>
                </div>
            </div>
        </section>
    );
}
